import { Base, variables } from '@bitrise/bitkit';
import {
  SeriesPointXY,
  SeriesPointXY0,
  useGetNearestPoint,
  Viz,
  VizHTML,
  VizHTMLArtboard,
  VizSVG,
  VizSVGAxisX,
  VizSVGAxisY,
  VizSVGGridLinesAxisY,
  VizSVGLine,
} from '@bitrise/bitviz';
import { VizSVGCircles } from '@bitrise/bitviz/lib/cjs/VizSVGCircles';
import { DataTimeSeries, OverviewMetric } from '@trace/types';
import max from 'lodash.max';
import React, { FunctionComponent, PointerEvent, useState } from 'react';
import getFormattedValue from '../../../utils/metric/getFormattedValue';
import getJoinedNullDataSet from '../../../utils/metric/getJoinedNullDataSet';
import { colorScale3, scaleZero } from '../../ViewVisualisations/colorScales';
import MessageFailing from '../../ViewVisualisations/MessageFailing';
import MessageFetching from '../../ViewVisualisations/MessageFetching';
import OverviewCard from '../OverviewCard/OverviewCard';
import OverviewTitle from '../OverviewCard/OverviewTitle';
import OverviewTooltip from '../OverviewTooltip/OverviewTooltip';
import OverviewVersionMetrics from './OverviewVersionMetrics';
import OverviewVersionTooltip from './OverviewVersionTooltip';

interface Props {
  data?: OverviewMetric;
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
  xAxisLabels: [string, number][];
}

const OverviewVersionAdoption: FunctionComponent<Props> = ({
  data,
  isError,
  isFetching,
  refetch,
  xAxisLabels,
}) => {
  const [[focusPoint, focusPointValues], setFocusPoint] = useState<
    [null | SeriesPointXY0, [string, number | null][]]
  >([null, []]);

  const sortedAppVersions = Object.entries(data?.appVersions || {})
    .map<[string, DataTimeSeries]>(([key, series]) => [
      key,
      getJoinedNullDataSet(series, { fill: 'zero', withEnd: true }),
    ])
    .sort((a, b) => (b[1][b[1].length - 1] || 0) - (a[1][a[1].length - 1] || 0));

  const getNearestPoint = useGetNearestPoint(
    data?.app.map((y, x) => ({ x, y: 0 })),
    { axis: 'x' },
  );

  const onPointerMove = (_: PointerEvent, point?: SeriesPointXY) => {
    if (point && point.x !== null) {
      const nearestPoint = getNearestPoint(point);

      if (nearestPoint && nearestPoint.x !== null) {
        const { x } = nearestPoint;
        const focusValues = sortedAppVersions.map(
          ([version, values]) => [version, values[x]] as [string, number | null],
        );

        const yMax = max(focusValues.map(([, value]) => value ?? null)) ?? null;
        setFocusPoint([{ x: nearestPoint.x, y: yMax, y0: 0 }, focusValues]);
        return;
      }
    }

    setFocusPoint([null, []]);
  };

  return (
    <OverviewCard>
      <OverviewTitle direction="vertical" title="Version Adoption">
        <OverviewVersionMetrics count={3} isFetching={isFetching} versions={sortedAppVersions} />
      </OverviewTitle>

      <Viz
        height={200}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerMove}
        paddingBottom={22}
        paddingLeft={34}
        paddingTop={22}
        xDomain={xAxisLabels.map((_, i) => i)}
        xDomainScale="band"
        xDomainPaddingOuter={0.5}
        yDomain={[0, 100]}
      >
        <VizHTML>
          <VizHTMLArtboard>
            <Base borderRadius="x2" backgroundColor="gray-1" width="100%" height="100%" />
          </VizHTMLArtboard>
        </VizHTML>

        <VizSVG>
          <VizSVGAxisX
            fill={variables.colorGray6}
            ticksOnly
            tickLineLength={0.001}
            format={(x) => xAxisLabels[x][0]}
          />

          <VizSVGAxisY
            fill={variables.colorGray6}
            flip
            format={(v) => getFormattedValue('percent', v)}
            tickLineLength={0.001}
            ticksOnly
            tickValues={[0, 50, 100]}
          />

          {!isError && !isFetching && (
            <VizSVGGridLinesAxisY
              animate
              stroke={variables.colorGray5}
              strokeDasharray="2 6"
              style={{ pointerEvents: 'none' }}
              tickValues={[25, 50, 75]}
            />
          )}

          {sortedAppVersions.map(([version, series], i, { length }) => (
            <VizSVGLine
              animate
              curve="curveMonotoneX"
              key={version}
              originY={0}
              series={series.map((y, x) => ({ x, y }))}
              stroke={colorScale3(scaleZero(i, length))}
              strokeWidth="2"
            />
          ))}

          {sortedAppVersions.map(([version, series], i, { length }) => (
            <VizSVGCircles
              animate
              fill={colorScale3(scaleZero(i, length))}
              key={version}
              originY={0}
              r={3}
              series={series.map((y, x) => ({ x, y }))}
            />
          ))}
        </VizSVG>

        <VizHTML>
          <VizHTMLArtboard>
            <MessageFetching fetching={isFetching} />
            <MessageFailing failing={isError} onFetch={refetch} />
          </VizHTMLArtboard>
        </VizHTML>

        {!!focusPoint && focusPoint.x !== null && (
          <OverviewTooltip point={focusPoint} timestamp={xAxisLabels[focusPoint.x][1]}>
            <OverviewVersionTooltip unit="percent" values={focusPointValues} />
          </OverviewTooltip>
        )}
      </Viz>
    </OverviewCard>
  );
};

export default OverviewVersionAdoption;
